import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bell, Mail, Monitor, Send } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { enableBrowserPush, disableBrowserPush, isPushSubscribed } from "@/lib/push";

export default function NotificationsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [org, setOrg] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    api.get("/notifications/events").then(r => setEvents(r.data));
    api.get("/notifications/preferences").then(r => setPrefs(r.data));
    api.get("/notifications/org").then(r => setOrg(r.data)).catch(() => {});
    isPushSubscribed().then(setPushOn);
  }, []);

  const togglePref = async (event_type, channel) => {
    const cur = prefs[event_type] || { in_app: true, email: false, push: false };
    const next = { ...cur, [channel]: !cur[channel] };
    setPrefs({ ...prefs, [event_type]: next });
    try { await api.patch("/notifications/preferences", { event_type, [channel]: next[channel] }); }
    catch { toast.error("Failed"); setPrefs({ ...prefs, [event_type]: cur }); }
  };

  const toggleBrowserPush = async () => {
    try {
      if (pushOn) { await disableBrowserPush(); setPushOn(false); toast.success("Browser push disabled"); }
      else { await enableBrowserPush(); setPushOn(true); toast.success("Browser push enabled"); }
    } catch (e) { toast.error(e.message || "Push setup failed"); }
  };

  const sendTest = async (channel) => {
    try { await api.post("/notifications/test", { channel }); toast.success(`Test ${channel} sent`); } catch { toast.error("Failed"); }
  };

  const updateOrg = async (key, value) => {
    const next = { ...org, [key]: value };
    setOrg(next);
    try { await api.patch("/notifications/org", { email_enabled: next.email_enabled, push_enabled: next.push_enabled, inapp_enabled: next.inapp_enabled, blocked_events: next.blocked_events || [] }); }
    catch { toast.error("Failed"); }
  };

  const groups = {};
  events.forEach(e => { (groups[e.group] ||= []).push(e); });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="notifications-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Settings</div>
        <h1 className="text-3xl font-[Outfit] font-semibold tracking-tight text-slate-900">Notifications</h1>
        <p className="text-sm text-slate-600 mt-1">Pick how you want to hear from QrewHR — in-app, email, browser push.</p>
      </div>

      {/* Channel setup */}
      <Card className="p-5 border-slate-200 shadow-none">
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4">Channel setup</div>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="p-3 rounded-md border border-slate-200 bg-slate-50/60">
            <div className="flex items-center gap-2 text-slate-800"><Bell className="w-4 h-4 text-cyan-600"/><span className="font-medium">In-app</span><Badge className="ml-auto bg-emerald-100 text-emerald-800 border-0 text-[10px]">Always on</Badge></div>
            <p className="text-[12px] text-slate-500 mt-2">Notifications appear in the header bell and /inbox.</p>
          </div>
          <div className="p-3 rounded-md border border-slate-200">
            <div className="flex items-center gap-2 text-slate-800"><Mail className="w-4 h-4 text-cyan-600"/><span className="font-medium">Email</span></div>
            <p className="text-[12px] text-slate-500 mt-2">Goes to <code className="bg-slate-100 px-1 rounded">{user?.email}</code> via Resend.</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => sendTest("email")} data-testid="test-email"><Send className="w-3 h-3 mr-1"/>Send test email</Button>
          </div>
          <div className="p-3 rounded-md border border-slate-200">
            <div className="flex items-center gap-2 text-slate-800"><Monitor className="w-4 h-4 text-cyan-600"/><span className="font-medium">Browser push</span></div>
            <p className="text-[12px] text-slate-500 mt-2">Native OS notifications even when tab is closed.</p>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant={pushOn ? "outline" : "default"} onClick={toggleBrowserPush} className={pushOn ? "" : "bg-slate-900"} data-testid="toggle-push">
                {pushOn ? "Disable" : "Enable"} push
              </Button>
              {pushOn && <Button size="sm" variant="outline" onClick={() => sendTest("push")}><Send className="w-3 h-3 mr-1"/>Test</Button>}
            </div>
          </div>
        </div>
      </Card>

      {/* Super admin org controls */}
      {isSuper && org && (
        <Card className="p-5 border-cyan-500/30 bg-cyan-500/5 shadow-none">
          <div className="text-xs uppercase tracking-wider font-semibold text-cyan-700 mb-3">Organization-wide controls (Super Admin)</div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: "inapp_enabled", l: "In-app", desc: "Kill switch for all in-app notifications" },
              { k: "email_enabled", l: "Email", desc: "Kill switch for all outbound emails" },
              { k: "push_enabled", l: "Browser push", desc: "Kill switch for all browser push" },
            ].map(c => (
              <div key={c.k} className="flex items-start gap-2 p-3 rounded-md bg-white border border-slate-200">
                <Switch checked={!!org[c.k]} onCheckedChange={(v) => updateOrg(c.k, v)}/>
                <div>
                  <div className="text-sm font-medium text-slate-900">{c.l}</div>
                  <div className="text-[11px] text-slate-500">{c.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Preferences matrix */}
      <div className="space-y-4">
        {Object.entries(groups).map(([g, items]) => (
          <Card key={g} className="border-slate-200 shadow-none overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider font-semibold text-slate-500">{g}</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="text-left px-4 py-2 font-semibold">Event</th>
                  <th className="text-center px-2 py-2 font-semibold">In-app</th>
                  <th className="text-center px-2 py-2 font-semibold">Email</th>
                  <th className="text-center px-2 py-2 font-semibold">Push</th>
                </tr>
              </thead>
              <tbody>
                {items.map(e => {
                  const p = prefs[e.key] || { in_app: true, email: false, push: false };
                  return (
                    <tr key={e.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pref-${e.key}`}>
                      <td className="px-4 py-2">
                        <div className="font-medium text-slate-900">{e.label}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{e.key}</div>
                      </td>
                      <td className="text-center px-2 py-2"><Switch checked={!!p.in_app} onCheckedChange={() => togglePref(e.key, "in_app")}/></td>
                      <td className="text-center px-2 py-2"><Switch checked={!!p.email} onCheckedChange={() => togglePref(e.key, "email")}/></td>
                      <td className="text-center px-2 py-2"><Switch checked={!!p.push} onCheckedChange={() => togglePref(e.key, "push")}/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        ))}
      </div>

      <Card className="p-4 border-amber-300 bg-amber-50 shadow-none">
        <div className="text-xs text-slate-700">
          <strong>Email delivery</strong> uses Resend. Super Admin must add <code className="bg-white px-1 rounded">RESEND_API_KEY</code> to backend env to enable production emails. Without it, email notifications are silently skipped (in-app still works).
        </div>
      </Card>
    </div>
  );
}
